/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
    BOTTOM_PADDING_PX,
    findAllParentIds,
    findNode,
    findNodes,
    findValidParentNode,
    FlatLibraryNode,
    flattenNodeTree,
    FolderBrowserView,
    GetInteractivity,
    getNodesBeingDisplayed,
    isAllNodesSelected,
    recursivelyAppendTopSelection,
    useTreeBrowser,
    VirtualScrollingTreeBrowserComponent,
    createBreadcrumb,
} from "@iventis/tree-browser";
import { useDispatch } from "react-redux";
import { Node } from "@iventis/domain-model/model/node";
import { useTheme } from "@mui/material";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TreeAction, StyledTreeActionsContainer, TreeBrowserHeaderComponent, ChangeTreeViewButton } from "@iventis/tree-browser/components/tree-browser-header";
import { fontSizes, StyledVerticalRule, styled, screenSizeBreakpoints } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Content } from "@iventis/translations/content/typed-content";
import { useMaxElementCount, canEdit, canRead, useFunctionality } from "@iventis/utilities";
import { userLocalStorageItems, userLocalStorageManager } from "@iventis/local-storage/src/user-local-storage";
import { CustomDialog, LimitChipRemaining, LoadingComponent, SimpleTooltip } from "@iventis/components";
import { NodeWithHierarchy, TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { DragAndDrop, DropZoneFill, GhostNodeComponent } from "@iventis/drag-and-drop";
import { toast } from "@iventis/toasts/src/toast";
import { LimitedProjectResource } from "@iventis/domain-model/model/limitedProjectResource";
import { authSlice } from "@iventis/permissions/src/auth.slice";
import { useProjectLimiter } from "@iventis/permissions/src/project-limiter-hook";
import { BreadCrumbComponent } from "@iventis/components/src/bread-crumb";
import { NodeEmptyComponent } from "@iventis/components/src/node-empty";
import { NoAccessOverlay } from "@iventis/permissions/src/upgrade-limit-overlay";
import { libraryListIcons, libraryNodeComponents, LibraryNodeExtraProps } from "./library-nodes";
import DeleteNodeComponent from "./delete-node-wrapper";
import { CreateNodeComponent } from "./create-node";
import { LibraryGalleryView, LibraryStyledGrid } from "./library-tile-view";
import { getLibrarySortByTreeSortId, getTreeSortByFieldSort, getTreeSortsFromLibrarySorts, getLibrarySorts } from "./library-sorting";
import MapBrowserHeader from "./map-browser-header";
import { AppBarSubMenuNode, StaticLibrary, calculateHeight, calculateVirtualListRemainingHeightInContainer } from "./mapping-library.helpers";
import { StaticLibraryHeader } from "./static-library-header";
import { BasicSkeletonTileNode } from "./basic-tile-node";
import { LibraryMoveDialog } from "./library-move-dialog";
import { LibraryCopyDialog } from "./library-copy-dialog";
import { StaticLibraryEmptyComponent } from "./static-library-empty";
import { RootDispatch } from "../state/root.store";

const LibraryComponent: React.FC<{
    libraries?: AppBarSubMenuNode[];
    allowedViewTypes: [FolderBrowserView, ...FolderBrowserView[]];
    defaultViewType?: FolderBrowserView;
    userId?: string;
    staticLibrary?: StaticLibrary;
    allowTreeActions: boolean;
    onLibraryChange: (newTree: Node, mainNodeId: string) => void;
}> = ({ libraries, allowedViewTypes, defaultViewType = allowedViewTypes[0], userId, staticLibrary, allowTreeActions, onLibraryChange }) => {
    const translate = useIventisTranslate();
    const dispatch = useDispatch<RootDispatch>();
    const functionality = useFunctionality();

    const theme = useTheme<Theme>();

    const [viewType, setViewType] = useState<FolderBrowserView>(defaultViewType);

    const setFolderBrowserView = (viewType: FolderBrowserView) => {
        userLocalStorageManager.set(userLocalStorageItems.folderBrowserView, userId, viewType);
        setViewType(viewType);
    };

    const librarySorts = useMemo(() => getLibrarySorts, []);

    const [{ expandedNodeIds, selectedNodeIds, sort, mainNodeId, tree, treeId, isLoadingTree }, treeBrowserContext] = useTreeBrowser(
        TreeBrowserDomainNodeContext,
        null,
        "expandedNodeIds",
        "selectedNodeIds",
        "sort",
        "mainNodeId",
        "tree",
        "treeId",
        "isLoadingTree"
    );

    const [maxElementContainerRef, maxNodeCount] = useMaxElementCount<HTMLDivElement>(245, 280, { roundUp: true }, [isLoadingTree, viewType]);

    // eslint-disable-next-line react/no-array-index-key
    const skeletons = new Array(maxNodeCount).fill(true).map((_, i) => <BasicSkeletonTileNode sequenceNumber={i} totalElementCount={maxNodeCount} key={i} />);

    const selectedLibrary = useMemo(() => libraries.find(({ id }) => id === treeId), [treeId]);

    const setLibrarySort = (newSortId: string) => {
        const newLibrarySort = getLibrarySortByTreeSortId(newSortId, librarySorts());
        userLocalStorageManager.set(userLocalStorageItems.folderBrowserSort, userId, { field: newLibrarySort.field, action: newLibrarySort.action });
        treeBrowserContext.setSort(newLibrarySort);
    };

    const displayTree = useMemo(() => findNode([tree], mainNodeId), [tree, mainNodeId]);

    const nodesToMove = useMemo(
        () => displayTree && recursivelyAppendTopSelection(displayTree, selectedNodeIds)?.filter((node) => [NodeType.Folder, NodeType.Map].includes(node.type)),
        [selectedNodeIds, displayTree]
    );

    const nodesToCopy = useMemo(
        () => displayTree && recursivelyAppendTopSelection(displayTree, selectedNodeIds)?.filter((node) => [NodeType.Folder, NodeType.Map].includes(node.type)),
        [selectedNodeIds, displayTree]
    );

    const canEditDisplayNode = useMemo(() => displayTree != null && canEdit(displayTree?.authorisation), [displayTree]);
    const canEditSelectedNodes = useMemo(() => selectedNodeIds.every((nodeId) => canEdit(findNode([tree], nodeId)?.authorisation)), [selectedNodeIds, tree]);
    const canMoveSelectedNodes = useMemo(() => nodesToMove?.length > 0 && nodesToMove.every((node) => canEdit(node.authorisation)), [nodesToMove]);
    const canCopySelectedNodes = useMemo(
        () => nodesToCopy?.length > 0 && flattenNodeTree(nodesToCopy, (n) => [NodeType.Folder, NodeType.Map].includes(n.type)).every((node) => canRead(node.authorisation)),
        [nodesToCopy]
    );

    const [modal, setModal] = useState<"create-node" | "move-nodes" | "copy-nodes" | null>(null);

    const folderPath = createBreadcrumb(mainNodeId, tree);

    const selectAllToggled = useMemo(() => isAllNodesSelected(displayTree, selectedNodeIds), [selectedNodeIds, displayTree]);

    const treeSorts = useMemo(() => getTreeSortsFromLibrarySorts(librarySorts()), []);

    const displayNodeIds = useMemo(() => (displayTree != null ? getNodesBeingDisplayed(displayTree, expandedNodeIds) : []), [displayTree, expandedNodeIds]);

    const dropZoneContainerRef = useRef<HTMLDivElement>(undefined);

    const [isDragging, setIsDragging] = useState<boolean>(false);

    const handleDragStart = () => {
        setIsDragging(true);
    };

    const getNodeInteractivity: GetInteractivity<Node> = useCallback(
        (node) => {
            // When dragging nodes should be disabled if the user does not have edit permission as they cannot drop on that node.
            const hasPermission = isDragging ? canEdit(node.authorisation) : canRead(node.authorisation);
            const isDraggable = canEdit(node.authorisation); // Needs to have write in order to drag node.
            return {
                selectable: { disabled: !allowTreeActions || !hasPermission, tooltip: undefined },
                disabled: !hasPermission,
                draggable: allowTreeActions && isDraggable,
                isNameClickable: canRead(node.authorisation),
            };
        },
        [isDragging, allowTreeActions]
    );

    const calculateNewFolder = (nodeId: string) => {
        const node = findNode([tree], nodeId);
        return findValidParentNode(tree, node);
    };

    const handleDragEnd = (nodes: Node[], over: { id: string }) => {
        setIsDragging(false);
        if (over == null || nodes.some(({ id }) => over.id === id)) {
            // Block drops into the abyss and into it's own
            return;
        }
        const folder = calculateNewFolder(over.id as string);
        if (nodes.every(({ parentId }) => parentId === folder.id)) {
            // Block drops into existing parent as nothing needs to happen. If at least one parent doesn't match then proceed
            return;
        }
        const parentIds = findAllParentIds(tree, folder.id);
        if (nodes.some(({ id }) => parentIds.includes(id))) {
            // Block drops into it's own children
            toast.info({ title: translate(Content.common.alert), message: translate(Content.library.cannot_drop_folder_into_its_sub_folder) });
            return;
        }
        treeBrowserContext.moveNodes(nodes, folder.id);
    };

    const findBottomFolder = (node: Node) => {
        if (!displayNodeIds.includes(node.id)) {
            // If it's not displayed, it must be a breadcrumb (which handles it's own drop zone)
            return undefined;
        }
        // Work from bottom up to find a node which is being displayed
        const flattenedNodes = flattenNodeTree(node).reverse();
        const bottomNode = flattenedNodes.find(({ id }) => displayNodeIds.includes(id));
        return bottomNode.id;
    };

    const getDraggingNodes = (nodeId) => {
        const draggingSelection = selectedNodeIds.includes(nodeId);
        if (draggingSelection) {
            const topSelection = recursivelyAppendTopSelection(displayTree, selectedNodeIds);
            // If the node the user has clicked down on is part of the current selection, drag all selected nodes
            return findNodes(
                displayTree.childNodes,
                topSelection.map((n) => n.id)
            );
        }
        // Else, drag the single node
        return [findNode(displayTree.childNodes, nodeId)];
    };

    const contentRef = useRef<HTMLDivElement>();

    const isSharedLibrary = selectedLibrary.type === NodeType.MappingLibrary && !staticLibrary;

    const [canUse, { projectUsage }] = useProjectLimiter();

    const canUseSharedLibrary = functionality.sharedWorkspace;

    const reachedTeamMapLimit = isSharedLibrary && !staticLibrary && !canUse("teamMaps");

    return (
        <>
            <DragAndDrop
                getItems={getDraggingNodes}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                calculateNewParent={calculateNewFolder}
                findBottomItem={findBottomFolder}
                idSuffixes={["breadcrumb"]}
                createOverlayComponent={(nodes) => {
                    const getIcon = (): React.ReactNode => {
                        if (nodes.length > 1) {
                            // Pick our base icon
                            const differingTypes = nodes.some((node) => node.type !== nodes[0].type);
                            const baseIcon = differingTypes ? libraryListIcons(theme)[NodeType.Folder] : libraryListIcons(theme)[nodes[0].type];
                            // Get a multiple item icon
                            return baseIcon;
                        }
                        return libraryListIcons(theme)[nodes[0].type];
                    };

                    const name = nodes.length > 1 ? translate(Content.library.drag_and_drop.moving_multiple_items, { count: nodes.length }) : nodes[0].name;

                    return <GhostNodeComponent count={nodes.length} getIconFunc={getIcon} name={name} />;
                }}
                calculateDropZoneHeight={(node) => {
                    // If the browser view is tile, then we don't need to calculate the height
                    if (viewType === FolderBrowserView.Tile) {
                        return undefined;
                    }
                    // If the parent passed is the tree, we don't need to calculate the height, just set it to undefined
                    return mainNodeId === node.id ? undefined : calculateHeight(node, expandedNodeIds);
                }}
            >
                <StyledLibraryHeader showBorderBottom={!allowTreeActions}>
                    {/* If using a static library, create our node in a personal mapping library, if thats not found then a shared mapping library */}
                    <MapBrowserHeader
                        parentNode={
                            staticLibrary == null
                                ? displayTree
                                : libraries.find((lib) => lib.type === NodeType.PersonalMappingLibrary) ?? libraries.find((lib) => lib.type === NodeType.MappingLibrary)
                        }
                        tree={
                            staticLibrary == null
                                ? tree
                                : libraries.find((lib) => lib.type === NodeType.PersonalMappingLibrary) ?? libraries.find((lib) => lib.type === NodeType.MappingLibrary)
                        }
                    />
                    {staticLibrary != null ? (
                        <StaticLibraryHeader staticLibrary={staticLibrary} isLoading={isLoadingTree} viewType={viewType} setViewType={setFolderBrowserView} />
                    ) : (
                        <BreadCrumbComponent<Node>
                            breadcrumbs={folderPath}
                            onElementClick={(node) => treeBrowserContext.setMainNode(node)}
                            headerIcon={selectedLibrary?.pageIcon}
                            className="folder-breadcrumb"
                            translatableContent={Content.library2.mappingLibraries}
                        />
                    )}
                </StyledLibraryHeader>

                {allowTreeActions && (
                    <TreeBrowserHeaderComponent
                        selectAllValue={selectAllToggled}
                        selectAll={() => treeBrowserContext.toggleSelectAll()}
                        sortMethods={treeSorts}
                        selectedSortMethod={getTreeSortByFieldSort(sort, treeSorts)}
                        onSortMethodChange={setLibrarySort}
                        disabledSort={isLoadingTree}
                        disabledSelection={isLoadingTree || !canEditDisplayNode || !displayTree?.childNodes.length}
                    >
                        <StyledTreeActionsContainer>
                            {isSharedLibrary && functionality.sharedWorkspace && window.innerWidth > screenSizeBreakpoints.extraSmall && (
                                <LimitChipRemaining currentUsage={projectUsage.teamMaps} maxUsage={functionality.maxTeamMaps} resource={LimitedProjectResource.TeamMap} />
                            )}
                            {allowedViewTypes?.length > 1 && (
                                <ChangeTreeViewButton
                                    disabled={isLoadingTree}
                                    viewType={viewType}
                                    onChange={setFolderBrowserView}
                                    text={translate(Content.library.folder_browser_view.while_in[viewType])}
                                />
                            )}
                            <TreeAction
                                dataCy="create-new-folder"
                                text={translate(Content.library.create_node.title, { nodeName: "folder" })}
                                onClick={() => setModal("create-node")}
                                disabled={isLoadingTree || !canEditDisplayNode || reachedTeamMapLimit}
                            >
                                <FontAwesomeIcon size="xs" icon={{ prefix: "far", iconName: "folder-plus" }} />
                                <span className="hidden">{translate(Content.library.create_node.title, { nodeName: "folder" })}</span>
                            </TreeAction>
                            <StyledVerticalRule margin={10} ruleHeight={30} />
                            <SimpleTooltip text={translate(Content.library2.permissions.no_write_permissions)} placement="top" disabled={canCopySelectedNodes}>
                                <span>
                                    <TreeAction
                                        dataTestId="copy-nodes"
                                        text={translate(Content.common.buttons.copy)}
                                        disabled={isLoadingTree || !canCopySelectedNodes || reachedTeamMapLimit}
                                        onClick={() => (functionality.plansCopying ? setModal("copy-nodes") : functionality.open?.("plansCopying"))}
                                    >
                                        <FontAwesomeIcon size="xs" icon={{ prefix: "far", iconName: "copy" }} />
                                    </TreeAction>
                                </span>
                            </SimpleTooltip>
                            <SimpleTooltip text={translate(Content.library2.permissions.no_write_permissions)} placement="top" disabled={canMoveSelectedNodes}>
                                <span>
                                    <TreeAction
                                        dataCy="move-nodes"
                                        text={translate(Content.common.buttons.move)}
                                        disabled={!canEditDisplayNode || isLoadingTree || !canMoveSelectedNodes || reachedTeamMapLimit}
                                        onClick={() => setModal("move-nodes")}
                                    >
                                        <FontAwesomeIcon size="xs" icon={{ prefix: "far", iconName: "arrows-up-down-left-right" }} />
                                    </TreeAction>
                                </span>
                            </SimpleTooltip>
                            <SimpleTooltip text={translate(Content.library2.permissions.no_write_permissions)} placement="top" disabled={canEditSelectedNodes}>
                                <span>
                                    <DeleteNodeComponent
                                        text={translate(Content.common.buttons.delete)}
                                        disabled={!canEditDisplayNode || isLoadingTree || selectedNodeIds.length === 0 || !canEditSelectedNodes}
                                        onDelete={(nodes: Node[]) => {
                                            const nodeCount = nodes.reduce((p, c) => p + flattenNodeTree(c).filter((n) => n.type === NodeType.Map).length, 0);
                                            dispatch(
                                                authSlice.actions.setUsage({
                                                    usage: "adjustTeamMaps",
                                                    adjustment: -nodeCount,
                                                })
                                            );
                                        }}
                                    />
                                </span>
                            </SimpleTooltip>
                        </StyledTreeActionsContainer>
                    </TreeBrowserHeaderComponent>
                )}

                {isLoadingTree ? (
                    viewType === FolderBrowserView.Tile ? (
                        <SkeletonContainer ref={maxElementContainerRef} data-testid="library-tile-loading-container">
                            {skeletons}
                        </SkeletonContainer>
                    ) : (
                        <LoadingComponent />
                    )
                ) : displayTree?.childNodes?.length === 0 ? (
                    staticLibrary != null ? (
                        <StaticLibraryEmptyComponent node={displayTree} staticLibrary={staticLibrary} />
                    ) : (
                        (canUseSharedLibrary || !isSharedLibrary) && <NodeEmptyComponent node={displayTree} testId={`spatial-library-${selectedLibrary?.type}`} />
                    )
                ) : (
                    <>
                        <StyledTreeBrowserContents data-testid={`spatial-library-${selectedLibrary?.type}`} ref={dropZoneContainerRef}>
                            {viewType === FolderBrowserView.List && (
                                <VirtualScrollingTreeBrowserComponent<FlatLibraryNode, LibraryNodeExtraProps>
                                    node={displayTree}
                                    openNodes={expandedNodeIds}
                                    components={libraryNodeComponents}
                                    extraProps={{ viewType, isDragging, disableActions: !allowTreeActions }}
                                    getInteractivity={getNodeInteractivity}
                                    containerRef={dropZoneContainerRef}
                                    createFooterComponent={(height) => (
                                        <RootNodeDropZone
                                            height={height < BOTTOM_PADDING_PX ? BOTTOM_PADDING_PX : height}
                                            id={displayTree.id}
                                            containerElementRef={dropZoneContainerRef}
                                        />
                                    )}
                                    calculateRemainingVirtualHeight={calculateVirtualListRemainingHeightInContainer}
                                />
                            )}
                            {viewType === FolderBrowserView.Tile && (
                                <>
                                    <RootNodeDropZoneTile height={contentRef?.current?.getBoundingClientRect()?.height} id={displayTree.id} containerElementRef={undefined} />
                                    <LibraryGalleryView
                                        tree={displayTree}
                                        components={libraryNodeComponents}
                                        getInteractivity={getNodeInteractivity}
                                        extraProps={{
                                            viewType,
                                            isDragging,
                                            rootLibraryName: selectedLibrary?.name,
                                            disableActions: !allowTreeActions,
                                        }}
                                    />
                                </>
                            )}
                        </StyledTreeBrowserContents>
                    </>
                )}
                {!canUseSharedLibrary && isSharedLibrary && <NoAccessOverlay upgradeType="sharedWorkspace" />}

                <CustomDialog maxWidth="sm" open={modal === "create-node"} fullWidth onClose={() => setModal(null)}>
                    {modal && (
                        <CreateNodeComponent
                            parentId={displayTree?.id}
                            onConfirm={(node, newTree, newParentId) => {
                                if (newTree) {
                                    treeBrowserContext.setWithExistingTree({ tree: newTree, mainNodeId: newParentId });
                                    treeBrowserContext.addNode(node);

                                    if (isSharedLibrary) dispatch(authSlice.actions.setUsage({ usage: "adjustTeamMaps", adjustment: 1 }));

                                    return Promise.resolve();
                                }
                                treeBrowserContext.addNode(node);
                                return Promise.resolve();
                            }}
                            onClose={() => setModal(null)}
                            nodeTypeToCreate={NodeType.Folder}
                            title={translate(Content.library.create_node.title, { nodeName: translate(Content.library.node_names.Folder) })}
                            fieldLabel={translate(Content.library.create_node.fieldLabel, { nodeName: translate(Content.library.node_names.Folder) })}
                            tree={tree}
                        />
                    )}
                </CustomDialog>
                {!staticLibrary && modal && (
                    <>
                        <LibraryMoveDialog
                            isOpen={modal === "move-nodes"}
                            closeModal={() => setModal(null)}
                            treeId={treeId}
                            onConfirmSwitchLibrary={(newTree, newParentId, nodesThatHaveMoved) => {
                                // Set the whole tree with what we already have
                                treeBrowserContext.setWithExistingTree({ tree: newTree, mainNodeId: newParentId });
                                // Make sure we fetch the node we're moving to to get all it's children
                                treeBrowserContext.expandNode(findNode([newTree], newParentId));
                                treeBrowserContext.toggleSelectNodes(nodesThatHaveMoved, undefined);
                                onLibraryChange(newTree, newParentId);
                            }}
                            onConfirm={(_, nodes, newParentId) => {
                                treeBrowserContext.moveNodes(nodes as NodeWithHierarchy[], newParentId);
                                treeBrowserContext.toggleSelectNodes(nodes, undefined);
                            }}
                            nodesToMove={nodesToMove}
                            canSwitchLibrary={tree.type === NodeType.PersonalMappingLibrary}
                        />
                        {modal === "copy-nodes" && (
                            <LibraryCopyDialog
                                isOpen={modal === "copy-nodes"}
                                closeModal={() => setModal(null)}
                                treeId={treeId}
                                onConfirm={(nodes, copyId, newParentId, includeMapObjects, keepPermissions) => {
                                    treeBrowserContext.copyNodes(nodes as NodeWithHierarchy[], copyId, newParentId, includeMapObjects, keepPermissions);
                                    treeBrowserContext.toggleSelectNodes(nodes, undefined);
                                }}
                                nodesToCopy={nodesToCopy}
                                canSwitchLibrary
                            />
                        )}
                    </>
                )}
            </DragAndDrop>
        </>
    );
};

export const StyledTreeBrowserContents = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1;
    overflow-y: auto;

    position: relative;

    .drop-zone-fill {
        flex: 1 1;
    }
`;

export const StyledLibraryHeader = styled.header<{ showBorderBottom: boolean }>`
    display: flex;
    align-items: center;
    display: flex;
    flex-direction: column;
    margin: 5px 0 5px 0;
    align-items: flex-end;

    ${({ theme, showBorderBottom }: { theme: Theme; showBorderBottom: boolean }) => (showBorderBottom ? `border-bottom: ${theme.shades.darkBorder} 1px solid;` : "")};

    .settings-button {
        height: ${({ theme }: { theme: Theme }) => theme.sizes.iconButtonWidth};
        width: ${({ theme }: { theme: Theme }) => theme.sizes.iconButtonWidth};
        font-size: ${fontSizes.small};
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .folder-breadcrumb {
        width: 100%;
        box-sizing: border-box;
    }
`;

const RootNodeDropZone = styled(DropZoneFill)<{ height: number }>`
    height: ${({ height }) => height}px;
`;

const RootNodeDropZoneTile = styled(DropZoneFill)<{ height: number }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    min-height: 100%;
    ${({ height }) => (height != null ? `height: ${height}px` : "bottom: 0")};
`;

const SkeletonContainer = styled(LibraryStyledGrid)`
    min-height: 100%;
`;

export default LibraryComponent;
